import { AppCard, AppPage } from '../../components/general';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { FeeConfigurationDetails } from './FeeConfigurationDetails';
import { FeeConfigurationViewModel } from '../../models/fees';
import { useFeeConfiguration, useFeeConfigurations, useHudLines, useHudLine, useFeeVersions } from '../../hooks/useFees';
import { buildFeeConfigurationCopyDraft } from '../../utils/fee-helper';

export const FeeConfigurationDetailsContainer = () => {
  const { feeConfigurationId: routeId } = useParams<{ feeConfigurationId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const feeConfigurationId = routeId ? parseInt(routeId, 10) : undefined;
  const isCreateMode = !feeConfigurationId;

  const copyState = location.state as { sourceConfig?: FeeConfigurationViewModel; hudLineCode?: string | null } | null;
  const sourceConfig = copyState?.sourceConfig;

  const versionIdFromQuery = useMemo(() => {
    const versionIdParam = searchParams.get('versionId');
    if (versionIdParam) {
      const versionId = parseInt(versionIdParam, 10);
      return !isNaN(versionId) ? versionId : null;
    }
    return null;
  }, [searchParams]);
  
  const { user, isLoading: authIsLoading } = useAuth();
  const { feeConfigurationQuery, updateFeeConfiguration } = useFeeConfiguration(feeConfigurationId);
  const { feeConfigurationsQuery, createFeeConfiguration } = useFeeConfigurations();
  const { hudLinesQuery } = useHudLines(null);
  const { versionsQuery } = useFeeVersions();
  const currentHudLineId = feeConfigurationQuery.data?.feeHudLineId;
  const { hudLineQuery: currentHudLineQuery } = useHudLine(currentHudLineId);

  const copyDraft = useMemo(() => {
    if (!isCreateMode || !sourceConfig) return null;
    const draftHudLineId =
      (copyState?.hudLineCode ? hudLinesQuery.data?.find((h) => h.hudLine === copyState.hudLineCode)?.feeHudLineId : undefined) ??
      sourceConfig.feeHudLineId;
    return buildFeeConfigurationCopyDraft(sourceConfig, draftHudLineId);
  }, [isCreateMode, sourceConfig, copyState?.hudLineCode, hudLinesQuery.data]);

  const isLoading =
    authIsLoading ||
    !user ||
    (isCreateMode ? false : feeConfigurationQuery.isLoading) ||
    feeConfigurationsQuery.isLoading ||
    hudLinesQuery.isLoading ||
    versionsQuery.isLoading ||
    (currentHudLineId !== undefined && currentHudLineQuery.isLoading);

  const allHudLines = useMemo(() => {
    const drafts = hudLinesQuery.data ?? [];
    const current = currentHudLineQuery.data;
    
    if (current) {
      const existsInDrafts = drafts.some((h) => h.feeHudLineId === current.feeHudLineId);
      if (!existsInDrafts) {
        return [current, ...drafts];
      }
    }
    
    return drafts;
  }, [hudLinesQuery.data, currentHudLineQuery.data]);

  if (isLoading) {
    return (
      <AppPage title={isCreateMode ? "Create Fee Configuration" : "Fee Configuration Details"} breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Fees', to: '/products/fees' }, { label: 'Fee Configurations', to: '/fees' }, { label: isCreateMode ? 'Create' : 'Details' }]}>
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (!isCreateMode && !feeConfigurationQuery.data) {
    return (
      <AppPage title="Fee Configuration Details" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Fees', to: '/products/fees' }, { label: 'Fee Configurations', to: '/fees' }, { label: 'Details' }]}>
        <AppCard>
          <div>Fee configuration not found</div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <FeeConfigurationDetails
      initialData={isCreateMode ? copyDraft : feeConfigurationQuery.data!}
      allFeeConfigurations={feeConfigurationsQuery.data ?? []}
      hudLines={allHudLines}
      versions={versionsQuery.data ?? []}
      saveFeeConfiguration={isCreateMode ? createFeeConfiguration : updateFeeConfiguration}
      isCreateMode={isCreateMode}
      versionIdFromQuery={versionIdFromQuery}
    />
  );
};

